import { Box } from '@chakra-ui/react';
import { FC, RefObject, useEffect, useRef } from 'react';
import { MODAL_INTERACTION_BLOCKER_Z_INDEX } from './constants';

type Props = {
	isActive: boolean;
	scrollContainerRef?: RefObject<HTMLElement | null>;
	zIndex?: number;
	dataTestId?: string;
	focusScrollContainerOnActivate?: boolean;
};

export const ModalInteractionBlocker: FC<Props> = ({
	isActive,
	scrollContainerRef,
	zIndex = MODAL_INTERACTION_BLOCKER_Z_INDEX,
	dataTestId = 'modal-interaction-blocker',
	focusScrollContainerOnActivate = true,
}) => {
	const lastTouchYRef = useRef<number | null>(null);

	useEffect(() => {
		if (!isActive || !focusScrollContainerOnActivate) {
			return;
		}

		const activeEl = document.activeElement as HTMLElement | null;
		activeEl?.blur?.();
		scrollContainerRef?.current?.focus?.();
	}, [focusScrollContainerOnActivate, isActive, scrollContainerRef]);

	if (!isActive) {
		return null;
	}

	return (
		<Box
			aria-hidden="true"
			data-testid={dataTestId}
			position="fixed"
			inset={0}
			zIndex={zIndex}
			bg="transparent"
			onWheel={(event) => {
				const scrollableEl = scrollContainerRef?.current;
				if (!scrollableEl) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				scrollableEl.scrollBy({ top: event.deltaY, left: 0 });
			}}
			onTouchStart={(event) => {
				event.preventDefault();
				event.stopPropagation();
				lastTouchYRef.current =
					event.touches && event.touches.length > 0
						? event.touches[0].clientY
						: null;
			}}
			onTouchMove={(event) => {
				const scrollableEl = scrollContainerRef?.current;
				const lastY = lastTouchYRef.current;
				const currentY =
					event.touches && event.touches.length > 0
						? event.touches[0].clientY
						: null;
				if (!scrollableEl || lastY === null || currentY === null) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				scrollableEl.scrollBy({ top: lastY - currentY, left: 0 });
				lastTouchYRef.current = currentY;
			}}
			onTouchEnd={(event) => {
				event.preventDefault();
				event.stopPropagation();
				lastTouchYRef.current = null;
			}}
			onTouchCancel={(event) => {
				event.preventDefault();
				event.stopPropagation();
				lastTouchYRef.current = null;
			}}
		/>
	);
};
